import { Box, SelectChangeEvent, Typography, useTheme } from "@mui/material";
import { FC, useEffect, useState } from "react";
import CustomDropdown from "../../../../components/CustomDropdown";
import CustomTextField from "../../../../components/CustomTextField";
import ModalBase from "../../../../components/ModalBase";
import StatusChecker from "../../../../components/StatusChecker";
import CustomButton from "../../../../components/CustomButton";
import { Country } from "../../../../shared/types/websiteTypes";
import { WebsiteManager } from "../../../../services/api/WebsiteManager";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ExternalApiManager } from "../../../../services/api/ExternalApiManager";
import { VerifyExternalApiResponse } from "../../../../shared/payloads/externalApiPayloads";
import { showFlashMessageWithTimeout } from "../../../../services/actions/flashMessage-actions";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useStyles } from "../AddApiKeyModal/addApiKeyModal.style";
import { ApiKeyModalProps } from "../AddApiKeyModal/types";

export const HeurekaApiKeyModal: FC<ApiKeyModalProps> = ({
  isOpen,
  onClose,
  createdWebId,
  websiteData,
  onAddKey,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [webName, setWebName] = useState<string>(
    websiteData?.heurekaName ?? "",
  );

  const [selectedCountry, setSelectedCountry] = useState<Country>(
    websiteData?.heurekaCountry ?? "cz",
  );

  const [apiKey, setApiKey] = useState<string>(
    websiteData?.heurekaApiKey ?? "",
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckLoading, setIsCheckLoading] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean | undefined>(
    websiteData?.heurekaApiKey ? true : undefined,
  );

  useEffect(() => {
    setWebName(websiteData?.heurekaName ?? "");
    setSelectedCountry(websiteData?.heurekaCountry ?? "cz");
    setApiKey(websiteData?.heurekaApiKey ?? "");
  }, [
    isOpen,
    websiteData?.heurekaApiKey,
    websiteData?.heurekaCountry,
    websiteData?.heurekaName,
  ]);

  const classes = useStyles();

  const handleWebNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWebName(event.target.value);
  };

  const onSelectedCountryChange = (event: SelectChangeEvent) => {
    setSelectedCountry(event.target.value as Country);
  };

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiKey(event.target.value);
  };

  const onAdd = async () => {
    if (createdWebId && selectedCountry && webName && apiKey && isVerified) {
      try {
        setIsLoading(true);
        const _websiteManager = WebsiteManager.getManager();
        const result = await _websiteManager.updateHeurekaAsync(
          createdWebId,
          selectedCountry,
          webName,
          apiKey,
        );
        if (result) {
          showFlashMessageWithTimeout(dispatch, t("onBoarding.apiKeyModal.added"), "success");
        }
        setIsLoading(false);
        if (onAddKey) onAddKey(apiKey);
        onClose();
      } catch (e: any) {
        setIsLoading(false);
        showFlashMessageWithTimeout(dispatch, e.toString(), "error");
      }
    } else {
      showFlashMessageWithTimeout(dispatch, t("onBoarding.errors.fillAllFields"), "info");
    }
  };

  const onCheck = async () => {
    if (apiKey.length === 0) {
      showFlashMessageWithTimeout(dispatch, t("onBoarding.errors.enterApiKey"), "info");
      return;
    }

    try {
      setIsCheckLoading(true);
      const _externalApiManager = ExternalApiManager.getManager();
      const result: VerifyExternalApiResponse =
        await _externalApiManager.verifyHeurekaExternalApi(
          apiKey,
          selectedCountry,
        );
      if (result.isVerified) {
        setIsVerified(true);
      } else {
        setIsVerified(false);
        showFlashMessageWithTimeout(dispatch, t("onBoarding.apiKeyModal.notVerified"), "error");
      }
      setIsCheckLoading(false);
    } catch (e: any) {
      setIsCheckLoading(false);
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} center>
      <Box className={classes.body}>
        <Box className={classes.iconContainer}>
          <CloseIcon
            style={{ color: theme.palette.natural.t2, cursor: "pointer" }}
            onClick={onClose}
          />
        </Box>
        <Typography className={classes.title}>
          {t(
            "campaign.notifications.settings.displayRules.heureka.heurekaApiKey",
          )}
        </Typography>
        <Box className={classes.dropdownContainer}>
          <CustomTextField
            style={{ marginRight: 24 }}
            fullWidth
            label={
              t("campaign.notifications.settings.displayRules.webName") ?? ""
            }
            value={webName}
            onChange={handleWebNameChange}
          />
          <CustomDropdown
            width={150}
            label={t(
              "campaign.notifications.settings.displayRules.heureka.country",
            )}
            value={selectedCountry}
            onChange={onSelectedCountryChange}
            id='country-dropdown'
            items={[
              { title: "cz", value: "cz" },
              { title: "sk", value: "sk" },
            ]}
          />
        </Box>
        <Box style={{ marginLeft: 32, marginRight: 32 }}>
          <CustomTextField
            style={{ marginRight: 24, marginTop: 60, marginBottom: 34 }}
            fullWidth
            label={
              t("campaign.notifications.settings.displayRules.apiKey") ?? ""
            }
            value={apiKey}
            onChange={handleApiKeyChange}
          />

          <Typography className={classes.listTitle}>
            {t(
              "campaign.notifications.settings.displayRules.heureka.howToAddApiKey.title",
            )}
          </Typography>

          <Box className={classes.rowWithMargin}>
            <Typography className={classes.listItemNumber}>1.</Typography>
            <Typography className={classes.listItemText}>
              {t(
                "campaign.notifications.settings.displayRules.heureka.howToAddApiKey.first",
              )}
            </Typography>
          </Box>
          <Box className={classes.rowWithMargin}>
            <Typography className={classes.listItemNumber}>2.</Typography>
            <Typography className={classes.listItemText}>
              {t(
                "campaign.notifications.settings.displayRules.heureka.howToAddApiKey.second",
              )}
            </Typography>
          </Box>
          <Box style={{ display: "flex", flexDirection: "row" }}>
            <Typography className={classes.listItemNumber}>3.</Typography>
            <Typography className={classes.listItemText}>
              {t(
                "campaign.notifications.settings.displayRules.heureka.howToAddApiKey.third",
              )}
            </Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Box className={classes.infoIconContainer}>
              <InfoOutlinedIcon style={{ color: theme.palette.natural.t2 }} />
            </Box>
            <Typography
              style={{ fontSize: 14, color: theme.palette.natural.t1 }}
            >
              {t(
                "campaign.notifications.settings.displayRules.heureka.howToAddApiKey.thirdHelp",
              )}
              .
            </Typography>
          </Box>
          <Box style={{ display: "flex", flexDirection: "row" }}>
            <Typography className={classes.listItemNumber}>4.</Typography>
            <Typography className={classes.listItemText}>
              {t(
                "campaign.notifications.settings.displayRules.heureka.howToAddApiKey.fourth",
              )}
            </Typography>
          </Box>
          <StatusChecker
            isActive={isVerified}
            isLoading={isCheckLoading}
            onClick={onCheck}
          />
          <Box className={classes.buttonContainer}>
            <CustomButton
              size='medium'
              isLoading={isLoading}
              title={
                websiteData?.heurekaApiKey || isVerified
                  ? t("saveChanges")
                  : t("add")
              }
              isDisabled={!isVerified}
              onClick={onAdd}
              style={{ width: 120, marginLeft: 20 }}
            />
            <CustomButton
              size='medium'
              isLoading={false}
              type='secondary'
              title={t("close")}
              onClick={onClose}
              style={{ width: 120 }}
            />
          </Box>
        </Box>
      </Box>
    </ModalBase>
  );
};
